import type {TrustedScriptURL, TrustedTypePolicy, TrustedTypePolicyFactory} from 'trusted-types/lib/index'

import {createPolicyMetricEmitter} from './policy-metrics'

let policy: Pick<TrustedTypePolicy, 'createScriptURL'> | undefined
let safeMetric = createPolicyMetricEmitter('github-worker')

function validateWorkerURL(input: string): string {
  if (typeof input !== 'string' || input.trim() === '') {
    throw new TypeError('Worker URL must be a non-empty string')
  }

  const url = new URL(input, window.location.href)
  if (
    (url.protocol !== 'https:' && url.protocol !== 'http:') ||
    url.origin !== window.location.origin ||
    url.username !== '' ||
    url.password !== ''
  ) {
    throw new TypeError('Worker URL must use same-origin HTTP(S) without credentials')
  }
  return url.href
}

function mintWorkerURL(input: string): string | TrustedScriptURL {
  const url = validateWorkerURL(input)
  const factory = (window as Window & {trustedTypes?: TrustedTypePolicyFactory}).trustedTypes
  if (factory === undefined) return url

  try {
    policy ??= factory.createPolicy('github-worker', {createScriptURL: validateWorkerURL})
    const trusted = policy.createScriptURL(url)
    safeMetric('TRUSTED_TYPES_POLICY_CALLED')
    return trusted
  } catch (error) {
    safeMetric('TRUSTED_TYPES_POLICY_ERROR')
    throw error
  }
}

// The DOM declarations do not accept TrustedScriptURL yet. These casts change only
// the TypeScript view; the native constructors receive the final branded object.
export function constructWorker(url: string, options?: WorkerOptions): Worker {
  return new Worker(mintWorkerURL(url) as string, options)
}

export function constructSharedWorker(url: string, options?: string | WorkerOptions): SharedWorker {
  return new SharedWorker(mintWorkerURL(url) as string, options)
}

/** Reset module state for isolated tests without exposing the policy or minter. */
export function _resetWorkerPolicyForTests(): void {
  policy = undefined
  safeMetric = createPolicyMetricEmitter('github-worker')
}
